/**
 * Products — admin control dashboard for the dual-category product
 * library. Tabs for Internal Prep vs Retail items. Manage names, prices,
 * shelf-life rules, ingredients, allergens and GTIN barcodes. Includes a
 * Tecor TR-422B test-print calibration button.
 */
import { useEffect, useMemo, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTecor } from "@/contexts/TecorContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ALLERGENS } from "@/lib/allergens";
import { AllergenDetectWarning } from "@/components/AllergenDetectWarning";
import { IngredientTemplates } from "@/components/IngredientTemplates";
import { NutritionEditor } from "@/components/NutritionEditor";
import { EMPTY_NUTRITION, cleanNutrition } from "@/lib/nutrition";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChefHat, ShoppingBag, FlaskConical, Usb, SlidersHorizontal, LayoutTemplate, AlertTriangle, Barcode } from "lucide-react";
import LabelTemplateEditor from "@/components/workflow/LabelTemplateEditor";
import CanvasLabelDesigner from "@/components/workflow/CanvasLabelDesigner";
import { LayoutThumb } from "@/components/workflow/LayoutThumb";
import CsvImportDialog from "@/components/workflow/CsvImportDialog";
import { logPrint } from "@/lib/printLog";

const STORAGE_OPTS = [
  { v: "refrigerated", l: "Refrigerated ≤ 5°C" },
  { v: "frozen", l: "Frozen ≤ -15°C" },
  { v: "hot_hold", l: "Hot hold ≥ 60°C" },
  { v: "ambient", l: "Ambient" },
];

function emptyProduct(category) {
  return {
    category,
    name: "",
    allergens: [],
    may_contain: [],
    storage: "refrigerated",
    notes: "",
    shelf_life_hours: 48,
    price_aud: 0,
    ingredients: "",
    barcode: "",
    best_before_days: 3,
    canvas_preset_id: "",
    nutrition: { ...EMPTY_NUTRITION },
  };
}

export default function Products() {
  const { activeSite, refresh } = useSite();
  const { user } = useAuth();
  const canEdit = user?.role !== "auditor";
  const isAdmin = user?.role === "admin";
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("prep");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // product or null (new)
  const [draft, setDraft] = useState(emptyProduct("prep"));
  const [barcodeCheck, setBarcodeCheck] = useState({ in_use: false, product_name: "", valid_ean13: null });

  const { supported, connected, connect, printB64 } = useTecor();

  const load = async () => {
    if (!activeSite) return;
    const { data } = await api.get("/products", withSite(activeSite.id));
    setProducts(data || []);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSite]);

  const prepItems = useMemo(() => products.filter((p) => p.category === "prep"), [products]);
  const retailItems = useMemo(() => products.filter((p) => p.category === "retail"), [products]);

  // Debounced duplicate-barcode check while the dialog is open (retail only).
  useEffect(() => {
    if (!dialogOpen || draft.category !== "retail" || !activeSite) {
      setBarcodeCheck({ in_use: false, product_name: "", valid_ean13: null });
      return;
    }
    const code = (draft.barcode || "").trim();
    if (!code) { setBarcodeCheck({ in_use: false, product_name: "", valid_ean13: null }); return; }
    let stale = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products/barcode-check",
          withSite(activeSite.id, { barcode: code, exclude_id: editing?.id || "" }));
        if (!stale) setBarcodeCheck(data);
      } catch { /* ignore */ }
    }, 400);
    return () => { stale = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.barcode, draft.category, dialogOpen, activeSite, editing]);

  const openNew = async (category) => {
    setEditing(null);
    setDraft(emptyProduct(category));
    setDialogOpen(true);
    if (category === "retail" && activeSite) {
      try {
        const { data } = await api.get("/products/next-barcode", withSite(activeSite.id));
        setDraft((d) => ({ ...d, barcode: d.barcode || data.barcode }));
      } catch { /* leave blank; backend auto-fills on save */ }
    }
  };
  const openEdit = (p) => {
    setEditing(p);
    setDraft({ ...emptyProduct(p.category), ...p });
    setDialogOpen(true);
  };

  const toggleAllergen = (id) =>
    setDraft((d) => ({
      ...d,
      allergens: d.allergens.includes(id)
        ? d.allergens.filter((x) => x !== id)
        : [...d.allergens, id],
    }));

  const toggleMayContain = (id) =>
    setDraft((d) => ({
      ...d,
      may_contain: (d.may_contain || []).includes(id)
        ? d.may_contain.filter((x) => x !== id)
        : [...(d.may_contain || []), id],
    }));

  const generateBarcode = async () => {
    if (!activeSite) return;
    try {
      const { data } = await api.get("/products/next-barcode", withSite(activeSite.id));
      setDraft((d) => ({ ...d, barcode: data.barcode }));
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't generate barcode");
    }
  };

  const backfillBarcodes = async () => {
    if (!activeSite) return;
    const missing = retailItems.filter((p) => !(p.barcode || "").trim()).length;
    if (missing === 0) { toast.info("Every retail product already has a barcode"); return; }
    if (!window.confirm(`Generate EAN-13 barcodes for ${missing} retail product${missing === 1 ? "" : "s"} without one?`)) return;
    try {
      const { data } = await api.post("/products/generate-barcodes", {}, withSite(activeSite.id));
      toast.success(`Assigned ${data.assigned} barcode${data.assigned === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't generate barcodes");
    }
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error("Name required");
      return;
    }
    try {
      const body = {
        category: draft.category,
        name: draft.name,
        allergens: draft.allergens,
        may_contain: draft.may_contain || [],
        storage: draft.storage,
        notes: draft.notes,
        shelf_life_hours: Number(draft.shelf_life_hours) || 48,
        price_aud: Number(draft.price_aud) || 0,
        ingredients: draft.ingredients,
        barcode: draft.barcode,
        best_before_days: Number(draft.best_before_days) || 0,
        canvas_preset_id: draft.canvas_preset_id || "",
        nutrition: draft.category === "retail" ? (draft.nutrition ? cleanNutrition(draft.nutrition) : null) : null,
      };
      if (editing) {
        await api.patch(`/products/${editing.id}`, body, withSite(activeSite.id));
        toast.success("Product updated");
      } else {
        await api.post("/products", body, withSite(activeSite.id));
        toast.success("Product added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`, withSite(activeSite.id));
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const bulkAssign = async (ids, presetId) => {
    try {
      await api.post(
        "/products/bulk-assign-layout",
        { product_ids: ids, canvas_preset_id: presetId },
        withSite(activeSite.id),
      );
      await load();
      toast.success(
        presetId
          ? `Layout assigned to ${ids.length} product${ids.length === 1 ? "" : "s"}`
          : `Cleared layout on ${ids.length} product${ids.length === 1 ? "" : "s"}`,
      );
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const testPrint = async () => {
    if (!supported) {
      toast.error("WebUSB not supported. Use Chrome or Edge.");
      return;
    }
    try {
      if (!connected) await connect();
      const { data } = await api.post(
        "/labels/render-tspl",
        { label_type: "test", copies: 1 },
        withSite(activeSite.id),
      );
      if (!data?.bytes_b64) {
        toast.error("Couldn't render test label.");
        return;
      }
      await printB64(data.bytes_b64);
      toast.success("Calibration label sent to Tecor TR-422B");
    } catch (err) {
      toast.error(err.message || formatErr(err.response?.data?.detail));
    }
  };

  const printBarcode = async (p) => {
    if (!supported) {
      toast.error("WebUSB not supported. Use Chrome or Edge.");
      return;
    }
    if (!(p.barcode || "").trim()) {
      toast.error("This product has no barcode. Add or generate one first.");
      return;
    }
    try {
      if (!connected) await connect();
      const { data } = await api.post(
        "/labels/render-tspl",
        { label_type: "barcode", product_name: p.name, barcode: p.barcode, price_aud: p.price_aud, copies: 1 },
        withSite(activeSite.id),
      );
      if (!data?.bytes_b64) {
        toast.error("Couldn't render barcode sticker.");
        return;
      }
      await printB64(data.bytes_b64);
      logPrint(activeSite?.id, { product_name: p.name, label_type: "retail", method: "tecor", copies: 1,
        payload: { label_type: "barcode", product_name: p.name, barcode: p.barcode, price_aud: p.price_aud, copies: 1 } });
      toast.success(`Barcode sticker for ${p.name} sent to Tecor`);
    } catch (err) {
      toast.error(err.message || formatErr(err.response?.data?.detail));
    }
  };

  const isRetail = draft.category === "retail";

  return (
    <div className="animate-fade-in" data-testid="products-page">
      <PageHeader
        title="Product Library"
        subtitle="Manage Internal Prep and Retail products, prices, shelf-life rules and GTIN barcodes for the Tecor TR-422B."
        action={
          <Button
            data-testid="products-test-print"
            variant="outline"
            className="gap-2 border-eucalyptus-700 text-eucalyptus-900"
            onClick={testPrint}
          >
            <FlaskConical className="w-4 h-4" /> Test print
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="products-tabs">
          <TabsTrigger value="prep" data-testid="products-tab-prep" className="gap-2">
            <ChefHat className="w-4 h-4" /> Internal Prep ({prepItems.length})
          </TabsTrigger>
          <TabsTrigger value="retail" data-testid="products-tab-retail" className="gap-2">
            <ShoppingBag className="w-4 h-4" /> Retail ({retailItems.length})
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="products-tab-templates" className="gap-2">
            <SlidersHorizontal className="w-4 h-4" /> Label Templates
          </TabsTrigger>
          <TabsTrigger value="canvas" data-testid="products-tab-canvas" className="gap-2">
            <LayoutTemplate className="w-4 h-4" /> Canvas Designer
          </TabsTrigger>
        </TabsList>

        <TabsContent value="prep">
          <ProductGrid
            items={prepItems} category="prep" canEdit={canEdit}
            onNew={() => openNew("prep")} onEdit={openEdit} onDelete={remove}
            presets={(activeSite?.canvas_layout_presets || {}).prep || []}
            onBulkAssign={bulkAssign}
          />
        </TabsContent>
        <TabsContent value="retail">
          {canEdit && (
            <div className="mt-4 mb-2 flex flex-wrap items-center gap-2">
              <CsvImportDialog activeSite={activeSite} onImported={load} />
              {isAdmin && (
                <Button
                  variant="outline" data-testid="retail-generate-barcodes"
                  onClick={backfillBarcodes}
                  className="gap-2 border-eucalyptus-900 text-eucalyptus-900 hover:bg-eucalyptus-100"
                >
                  <Usb className="w-4 h-4" /> Generate barcodes
                </Button>
              )}
            </div>
          )}
          <ProductGrid
            items={retailItems} category="retail" canEdit={canEdit}
            onNew={() => openNew("retail")} onEdit={openEdit} onDelete={remove}
            presets={(activeSite?.canvas_layout_presets || {}).retail || []}
            onBulkAssign={bulkAssign}
            onPrintBarcode={printBarcode}
          />
        </TabsContent>
        <TabsContent value="templates">
          <LabelTemplateEditor activeSite={activeSite} refresh={refresh} />
        </TabsContent>
        <TabsContent value="canvas">
          <CanvasLabelDesigner activeSite={activeSite} refresh={refresh} />
        </TabsContent>
      </Tabs>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit" : "New"} {isRetail ? "retail" : "prep"} product
            </DialogTitle>
            <DialogDescription>
              {isRetail
                ? "Set price, best-before window, GTIN barcode, ingredients and allergens."
                : "Set the shelf life (drives auto Use-By), storage and allergens."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                data-testid="product-name" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>

            {isRetail ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Price (AUD)</Label>
                  <Input
                    data-testid="product-price" type="number" step="0.01" min="0"
                    value={draft.price_aud}
                    onChange={(e) => setDraft({ ...draft, price_aud: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Best-before (days)</Label>
                  <Input
                    data-testid="product-bestbefore-days" type="number" min="0"
                    value={draft.best_before_days}
                    onChange={(e) => setDraft({ ...draft, best_before_days: e.target.value })}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Barcode (GTIN / EAN-13)</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="product-barcode" className="font-mono flex-1" value={draft.barcode}
                      onChange={(e) => setDraft({ ...draft, barcode: e.target.value })}
                      placeholder="9312345678907"
                    />
                    <Button
                      type="button" variant="outline" data-testid="product-barcode-generate"
                      onClick={generateBarcode}
                      className="shrink-0 border-eucalyptus-900 text-eucalyptus-900 hover:bg-eucalyptus-100"
                    >
                      Generate
                    </Button>
                  </div>
                  <p className="text-[11px] text-charcoal-soft">Auto-filled with an in-store EAN-13 on new items — override with the real GTIN when you have one.</p>
                  {barcodeCheck.in_use && (
                    <p data-testid="product-barcode-dup" className="text-[12px] font-semibold text-ochre-600 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Already used by “{barcodeCheck.product_name}”.
                    </p>
                  )}
                  {barcodeCheck.valid_ean13 === false && !barcodeCheck.in_use && (
                    <p data-testid="product-barcode-invalid" className="text-[12px] text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Not a valid EAN-13 check digit — double-check the number.
                    </p>
                  )}
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Ingredients</Label>
                  <Textarea
                    data-testid="product-ingredients" value={draft.ingredients}
                    onChange={(e) => setDraft({ ...draft, ingredients: e.target.value })}
                  />
                  <IngredientTemplates
                    siteId={activeSite?.id}
                    value={draft.ingredients}
                    onApply={(text) => setDraft((d) => ({ ...d, ingredients: text }))}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Shelf life (hours) — used to auto-calc Use-By</Label>
                <Input
                  data-testid="product-shelf" type="number" min="1"
                  value={draft.shelf_life_hours}
                  onChange={(e) => setDraft({ ...draft, shelf_life_hours: e.target.value })}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Storage</Label>
              <Select value={draft.storage} onValueChange={(v) => setDraft({ ...draft, storage: v })}>
                <SelectTrigger data-testid="product-storage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STORAGE_OPTS.map((o) => (
                    <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Canvas layout (optional)</Label>
              <Select
                value={draft.canvas_preset_id || "none"}
                onValueChange={(v) => setDraft({ ...draft, canvas_preset_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="product-canvas-preset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Default ({isRetail ? "retail" : "prep"} template / canvas)</SelectItem>
                  {((activeSite?.canvas_layout_presets || {})[draft.category] || []).map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`product-canvas-preset-opt-${p.id}`}>
                      <span className="flex items-center gap-2">
                        <LayoutThumb layout={p.layout} />
                        {p.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-charcoal-soft">Auto-applies this saved design when staff print this product. Manage designs in Canvas Designer.</p>
            </div>

            <div className="space-y-2">
              <Label>Allergens</Label>
              <div className="grid grid-cols-2 gap-2 surface-alt p-3 rounded-lg">
                {ALLERGENS.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      data-testid={`product-allergen-${a.id}`}
                      checked={draft.allergens.includes(a.id)}
                      onCheckedChange={() => toggleAllergen(a.id)}
                    />
                    {a.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>May be present (precautionary)</Label>
              <p className="text-xs text-charcoal-soft -mt-1">Cross-contact risk — prints a "May be present:" declaration line.</p>
              <div className="grid grid-cols-2 gap-2 surface-alt p-3 rounded-lg">
                {ALLERGENS.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      data-testid={`product-maycontain-${a.id}`}
                      checked={(draft.may_contain || []).includes(a.id)}
                      onCheckedChange={() => toggleMayContain(a.id)}
                    />
                    {a.label}
                  </label>
                ))}
              </div>
            </div>

            {isRetail && (
              <AllergenDetectWarning
                ingredients={draft.ingredients}
                declared={draft.allergens}
                mayContain={draft.may_contain}
                onAddContains={(id) => setDraft((d) => ({ ...d, allergens: [...new Set([...d.allergens, id])] }))}
                onAddMayContain={(id) => setDraft((d) => ({ ...d, may_contain: [...new Set([...(d.may_contain || []), id])] }))}
                onAddAllContains={(ids) => setDraft((d) => ({ ...d, allergens: [...new Set([...d.allergens, ...ids])] }))}
              />
            )}

            {isRetail && (
              <NutritionEditor
                value={draft.nutrition}
                onChange={(n) => setDraft((d) => ({ ...d, nutrition: n }))}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="product-save" className="bg-ochre-600 hover:bg-ochre-400 text-white" onClick={save}>
              {editing ? "Save changes" : "Add product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductGrid({ items, category, canEdit, onNew, onEdit, onDelete, presets = [], onBulkAssign, onPrintBarcode }) {
  const isRetail = category === "retail";
  const [sel, setSel] = useState(new Set());
  const [bulkPreset, setBulkPreset] = useState("none");
  const [applying, setApplying] = useState(false);

  const toggle = (id) =>
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const allSelected = items.length > 0 && items.every((p) => sel.has(p.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(items.map((p) => p.id)));

  const apply = async () => {
    setApplying(true);
    await onBulkAssign([...sel], bulkPreset === "none" ? "" : bulkPreset);
    setSel(new Set());
    setBulkPreset("none");
    setApplying(false);
  };

  return (
    <div className="mt-4">
      {canEdit && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Button
            data-testid={`product-new-${category}`}
            className="gap-2 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white"
            onClick={onNew}
          >
            <Plus className="w-4 h-4" /> New {isRetail ? "retail" : "prep"} product
          </Button>
          {items.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-charcoal-soft cursor-pointer" data-testid={`product-select-all-${category}`}>
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} /> Select all
            </label>
          )}
        </div>
      )}

      {canEdit && sel.size > 0 && (
        <div className="surface p-3 mb-4 flex flex-wrap items-center gap-3" data-testid={`product-bulk-bar-${category}`}>
          <span className="text-sm font-semibold">{sel.size} selected</span>
          <span className="text-sm text-charcoal-soft">→ assign layout:</span>
          <Select value={bulkPreset} onValueChange={setBulkPreset}>
            <SelectTrigger data-testid={`product-bulk-preset-${category}`} className="h-9 w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Default (clear assignment)</SelectItem>
              {presets.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`product-bulk-opt-${p.id}`}>
                  <span className="flex items-center gap-2"><LayoutThumb layout={p.layout} />{p.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid={`product-bulk-apply-${category}`}
            disabled={applying}
            className="gap-1 bg-ochre-600 hover:bg-ochre-400 text-white"
            onClick={apply}
          >
            {applying ? "Applying…" : `Apply to ${sel.size}`}
          </Button>
          <button className="text-sm text-eucalyptus-800 underline" onClick={() => setSel(new Set())}>clear</button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="surface p-8 text-center text-charcoal-soft text-sm">
          No {isRetail ? "retail" : "prep"} products yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className={`surface p-5 space-y-2 ${sel.has(p.id) ? "ring-2 ring-ochre-500" : ""}`} data-testid={`product-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  {canEdit && (
                    <Checkbox
                      data-testid={`product-select-${p.id}`}
                      checked={sel.has(p.id)}
                      onCheckedChange={() => toggle(p.id)}
                      className="mt-1"
                    />
                  )}
                  <h3 className="font-display font-bold text-base leading-tight truncate">{p.name}</h3>
                </div>
                {isRetail ? (
                  <Badge className="bg-ochre-600 text-white shrink-0">${Number(p.price_aud || 0).toFixed(2)}</Badge>
                ) : (
                  <Badge variant="outline" className="shrink-0">{p.shelf_life_hours}h</Badge>
                )}
              </div>
              <div className="text-xs text-charcoal-soft capitalize">{(p.storage || "").replace("_", " ")}</div>
              {p.canvas_preset_id && (
                <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-eucalyptus-100 text-eucalyptus-900 px-1.5 py-0.5 rounded" data-testid={`product-layout-badge-${p.id}`}>
                  <LayoutTemplate className="w-3 h-3" /> {(presets.find((x) => x.id === p.canvas_preset_id) || {}).name || "Custom layout"}
                </div>
              )}
              {isRetail && p.barcode && (
                <div className="font-mono text-xs flex items-center gap-1">
                  <Usb className="w-3 h-3" /> {p.barcode}
                </div>
              )}
              {p.allergens?.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {p.allergens.map((a) => (
                    <span key={a} className="text-[10px] uppercase font-bold bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                      {a.replace("_", " ")}
                    </span>
                  ))}
                </div>
              )}
              {canEdit && (
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm" variant="outline" className="gap-1 flex-1"
                    data-testid={`product-edit-${p.id}`} onClick={() => onEdit(p)}
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </Button>
                  {isRetail && onPrintBarcode && (
                    <Button
                      size="sm" variant="outline"
                      className="gap-1 border-eucalyptus-900 text-eucalyptus-900 hover:bg-eucalyptus-100"
                      data-testid={`product-print-barcode-${p.id}`} onClick={() => onPrintBarcode(p)}
                      title="Print just the barcode sticker to the Tecor"
                    >
                      <Barcode className="w-3 h-3" /> Barcode
                    </Button>
                  )}
                  <Button
                    size="sm" variant="outline" className="gap-1 text-red-700 border-red-200 hover:bg-red-50"
                    data-testid={`product-delete-${p.id}`} onClick={() => onDelete(p)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
